import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { Autocomplete } from '@shopify/polaris';

const propTypes = {
  options: PropTypes.array.isRequired, // [{ label, value }], same shape a Select takes
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired, // called (value, id), like Polaris Select, so handlers port over
  id: PropTypes.string,

  label: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
}

const defaultProps = {
  value: '',
  id: undefined,
  label: '',
  placeholder: 'Escribí para buscar',
  disabled: false,
  error: undefined,
}

// A Select you can type into, for lists long enough that scanning them with the mouse is the slow
// part - couriers, teams. Filtering happens here because the options are already loaded; the shared
// Autocomplete in this folder is the other case, where the list lives on the server.
//
// Keep using Select for short lists: with four options, typing is slower than picking.
const SearchableSelect = (props) => {
  const { options, value, onChange, id, label, placeholder, disabled, error } = props;

  const labelFor = (optionValue) => options.find((option) => option.value === optionValue)?.label ?? '';
  const [search, setSearch] = useState(labelFor(value));

  // The selected value can change from outside (a form reset, a dependent field reloading its
  // options), and the text box has to follow it or it would show a label nobody selected.
  useEffect(() => { setSearch(labelFor(value)); }, [value, options]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term || term === labelFor(value).toLowerCase()) return options;

    return options.filter((option) => String(option.label).toLowerCase().includes(term));
  }, [options, search, value]);

  return (
    <Autocomplete
      options={filtered}
      selected={value ? [value] : []}
      onSelect={(selected) => {
        onChange(selected[0] ?? '', id);
        setSearch(labelFor(selected[0]));
      }}
      textField={
        <Autocomplete.TextField
          id={id}
          label={label}
          value={search}
          onChange={setSearch}
          // Typing without picking anything left the text saying one courier while the form still
          // held the previous id - the task would have gone to whoever was selected before, under a
          // name nobody saw. Leaving the field puts the selected label back.
          onBlur={() => setSearch(labelFor(value))}
          placeholder={placeholder}
          disabled={disabled}
          error={error}
          autoComplete="off"
        />
      }
    />
  );
};

SearchableSelect.propTypes = propTypes;
SearchableSelect.defaultProps = defaultProps;

export default SearchableSelect;
